import { useRef, useState, useEffect, useMemo } from 'react';
import { ArrowRight } from 'react-feather';
import { useNavigate } from 'react-router';
import {
        AgentModeToggle,
        type AgentMode,
} from '../components/agent-mode-toggle';
import { useAuthGuard } from '../hooks/useAuthGuard';

export default function Home() {
        const navigate = useNavigate();
        const { requireAuth } = useAuthGuard();
        const textareaRef = useRef<HTMLTextAreaElement>(null);
        const [agentMode, setAgentMode] = useState<AgentMode>('deterministic');
        
        
        const placeholderPhrases = useMemo(() => [
                "todo list app",
                "F1 fantasy game",
                "personal finance tracker"
        ], []);
        const [currentPlaceholderPhraseIndex, setCurrentPlaceholderPhraseIndex] = useState(0);
        const [currentPlaceholderText, setCurrentPlaceholderText] = useState("");
        const [isPlaceholderTyping, setIsPlaceholderTyping] = useState(true);

        const handleCreateApp = (query: string, mode: AgentMode) => {
                const encodedQuery = encodeURIComponent(query);
                const encodedMode = encodeURIComponent(mode);
                const intendedUrl = `/chat/new?query=${encodedQuery}&agentMode=${encodedMode}`;

                if (
                        !requireAuth({
                                requireFullAuth: true,
                                actionContext: 'to create applications',
                                intendedUrl: intendedUrl,
                        })
                ) {
                        return;
                }

                // User is already authenticated, navigate immediately
                navigate(intendedUrl);
        };

        // Auto-resize textarea based on content
        const adjustTextareaHeight = () => {
                if (textareaRef.current) {
                        textareaRef.current.style.height = 'auto';
                        const scrollHeight = textareaRef.current.scrollHeight;
                        const maxHeight = 300; // Maximum height in pixels
                        textareaRef.current.style.height =
                                Math.min(scrollHeight, maxHeight) + 'px';
                }
        };

        useEffect(() => {
                adjustTextareaHeight();
        }, []);
        
        // Typewriter effect
        useEffect(() => {
                const currentPhrase = placeholderPhrases[currentPlaceholderPhraseIndex];
                
                if (isPlaceholderTyping) {
                        if (currentPlaceholderText.length < currentPhrase.length) {
                                const timeout = setTimeout(() => {
                                        setCurrentPlaceholderText(currentPhrase.slice(0, currentPlaceholderText.length + 1));
                                }, 100); // Typing speed
                                return () => clearTimeout(timeout);
                        } else {
                                // Pause before erasing
                                const timeout = setTimeout(() => {
                                        setIsPlaceholderTyping(false);
                                }, 2000); // Pause duration
                                return () => clearTimeout(timeout);
                        }
                } else {
                        if (currentPlaceholderText.length > 0) {
                                const timeout = setTimeout(() => {
                                        setCurrentPlaceholderText(currentPlaceholderText.slice(0, -1));
                                }, 50); // Erasing speed
                                return () => clearTimeout(timeout);
                        } else {
                                // Move to next phrase
                                setCurrentPlaceholderPhraseIndex((prev) => (prev + 1) % placeholderPhrases.length);
                                setIsPlaceholderTyping(true);
                        }
                }
        }, [currentPlaceholderText, currentPlaceholderPhraseIndex, isPlaceholderTyping, placeholderPhrases]);
        return (
                <div className="flex flex-col items-center size-full">
                        <div className="w-full max-w-[720px] overflow-hidden mt-[140px] relative z-10">
                                {/* Purple glow effect */}
                                <div className="absolute inset-0 bg-purple-600/10 blur-[100px] rounded-full pointer-events-none"></div>
                                
                                <div className="z-10 flex flex-col items-center px-6 py-8">
                                        <h1 className="text-transparent bg-clip-text bg-gradient-to-r from-[#9F55FF] to-[#FF46C7] leading-[1.1] font-medium text-[60px] tracking-[-1.5px] w-full mt-0 mb-4 text-center">
                                                What should we build today?
                                        </h1>

                                        <form
                                                method="POST"
                                                onSubmit={(e) => {
                                                        e.preventDefault();
                                                        const query = textareaRef.current!.value;
                                                        handleCreateApp(query, agentMode);
                                                }}
                                                className="bg-[#13111C]/60 backdrop-blur-xl w-full min-h-[150px] z-10 flex flex-col justify-between shadow-2xl p-5 rounded-2xl border border-white/10 focus-within:border-purple-500/50 transition-colors"
                                        >
                                                <textarea
                                                        className="bg-transparent text-[16px] text-white placeholder-white/40 w-full h-12 z-20 block resize-none p-0 border-none outline-none focus:ring-0"
                                                        name="query"
                                                        placeholder={`Create a ${currentPlaceholderText}`}
                                                        ref={textareaRef}
                                                        onChange={adjustTextareaHeight}
                                                        onInput={adjustTextareaHeight}
                                                        onKeyDown={(e) => {
                                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                                        e.preventDefault();
                                                                        const query = textareaRef.current!.value;
                                                                        handleCreateApp(query, agentMode);
                                                                }
                                                        }}
                                                />
                                                <div className="flex items-center justify-between mt-4 pt-1">
                                                        {import.meta.env.VITE_AGENT_MODE_ENABLED ? (
                                                                <AgentModeToggle
                                                                        value={agentMode}
                                                                        onChange={setAgentMode}
                                                                        className="flex-1"
                                                                />
                                                        ) : (
                                                                <div className="flex gap-2"></div>
                                                        )}

                                                        <div className="flex items-center justify-end ml-4">
                                                                <button
                                                                        type="submit"
                                                                        className="bg-purple-600 hover:bg-purple-500 text-white transition-colors p-2 rounded-lg flex items-center justify-center"
                                                                >
                                                                        <ArrowRight className="w-5 h-5" />
                                                                </button>
                                                        </div>
                                                </div>
                                        </form>
                                </div>
                        </div>
                </div>
        );
}
